import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Generic pagination hook for client-side pagination.
 *
 * Provides pagination state, handlers, and paginated items for any array of data.
 * Automatically resets the page if it goes out of bounds when the data changes.
 *
 * @template T - The type of items in the array
 * @param items - Array of items to paginate
 * @param defaultRowsPerPage - Default number of rows per page (defaults to 25)
 *
 * @example
 * ```tsx
 * const pagination = UsePagination(sortedData, 10);
 * 
 * <TablePagination
 *   count={pagination.totalCount}
 *   page={pagination.page}
 *   rowsPerPage={pagination.rowsPerPage}
 *   onPageChange={pagination.handleChangePage}
 *   onRowsPerPageChange={pagination.handleChangeRowsPerPage}
 * />
 * ```
 */
export function UsePagination<T>(
	items: T[],
	defaultRowsPerPage = 25
) {
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

	// Reset page if it goes out of bounds (like legacy table)
	useEffect(() => {
		const maxPage = Math.max(
			0,
			Math.ceil(items.length / rowsPerPage) - 1
		);
		if (page > maxPage) {
			setPage(0);
		}
	}, [items.length, rowsPerPage, page]);

	// Paginated items (manual slicing like legacy)
	const pagedItems = useMemo(() => {
		const start = page * rowsPerPage;
		const end = start + rowsPerPage;
		return items.slice(start, end);
	}, [items, page, rowsPerPage]);

	const handleChangePage = useCallback(
		(_: unknown, newPage: number) => {
			setPage(newPage);
		},
		[]
	);

	const handleChangeRowsPerPage = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const val = parseInt(event.target.value, 10);
			if (!isNaN(val)) {
				setRowsPerPage(val);
				setPage(0);
			}
		},
		[]
	);

	return {
		page,
		rowsPerPage,
		pagedItems,
		handleChangePage,
		handleChangeRowsPerPage,
		totalCount: items.length,
	};
}

